import {
  Calculation,
  CostAccountingDimension,
  CostAccountingDimensionDefinition,
  IrRow,
  Numeric,
  Translations
} from "@salaxy/core";

import { RowComponent } from "../../../ng1/node_modules/tabulator-tables";

/**
 * Helper class used to create CostCenterReport (kustannuspaikkaraportti) config for sxy-table.
 */
export class CostCenterReportConfigGenerator {
  /**
   * Generates config for SxyTable component.
   * @param calcs Calculations for report
   * @returns Configuration for SxyTable component
   */
  public static getConfig(calcs: Calculation[], sysDims: CostAccountingDimensionDefinition[]): { options: any, include: any[], data: any[] } {
    let parentIndex = 0;

    const dimensions = this.getDimensions(sysDims);

    return {
      options: {
        selectableRows: false,
        layout: "fitColumns",
        dataTree: true,
        dataTreeStartExpanded: [true, true],
        dataTreeChildIndent: 30,
        rowFormatter: (row: RowComponent) => {
          const elem = row.getElement();
          const parent = row.getTreeParent();

          if (!parent) {
            // Add the same class for parent and nested (child) rows.
            const oddOrEvenClass = (parentIndex++ % 2 == 0) ? "nested-odd" : "nested-even";
            elem.classList.add(oddOrEvenClass);
            this.addClassToChildRows(row, oddOrEvenClass);
          }
        }
      },
      include: this.getColConfig(dimensions),
      data: this.getData(calcs, dimensions),
    };
  }

  /**
   * Adds same css class to child rows, recursively.
   * @param row Row element.
   * @param cssClass Either "nested-odd" or "nested-even".
   */
  private static addClassToChildRows(row: RowComponent, cssClass: string) {
    (row.getTreeChildren() || []).forEach(child => {
      child.getElement().classList.add(cssClass);
      this.addClassToChildRows(child, cssClass);
    });
  }

  /**
   * Get all the dimensions from system settings.
   * @param dims System dimensions.
   * @returns Dimensions.
   */
  public static getDimensions(dims: CostAccountingDimensionDefinition[]): any[] {
    const dimensions: any = [];

    for (const dim of dims) {
      if (!dim.options?.length) {
        continue;
      }

      for (const option of dim.options) {
        dimensions.push({
          label: `${dim.label} (${option.value})`,
          field: option.value
        });
      }
    }

    return dimensions;
  }

  /**
   * Headers for the report (both browser and excel).
   * @returns Array of headers.
   */
  public static getHeaders(): { field: string, title: string }[] {
    return [
      { field: "worker", title: Translations.get("SALAXY.REPORTS.costCenterReport.worker") },
      { field: "rowType", title: Translations.get("SALAXY.REPORTS.costCenterReport.rowType") },
      { field: "type", title: Translations.get("SALAXY.REPORTS.costCenterReport.type") },
      { field: "irCode", title: Translations.get("SALAXY.REPORTS.costCenterReport.irCode") },
      { field: "count", title: Translations.get("SALAXY.REPORTS.costCenterReport.count") },
      { field: "price", title: Translations.get("SALAXY.REPORTS.costCenterReport.price") },
      { field: "total", title: Translations.get("SALAXY.REPORTS.costCenterReport.total") },
    ];
  }

  /**
   * Creates column config for tabulator table.
   * @param dimensions System dimensions.
   * @returns Column configuration.
   */
  private static getColConfig(dimensions: any): any {
    const headers = this.getHeaders();
    const dimensionHeaders = dimensions.map(d => ({ field: d.label }));

    const fieldConfigs = {
      worker: {
        frozen: true,
        minWidth: 100,
      },
      price: this.getCurrencySettings(),
      total: this.getCurrencySettings(),
      count: {
        headerHozAlign: "right",
        hozAlign: "right"
      },
    };

    const colConfig = [
      ...headers.map(header => ({
        ...header,
        ...fieldConfigs[header.field]
      })),
      ...dimensionHeaders.map(header => ({
        ...header,
        ...this.getCurrencySettings()
      }))
    ];

    return colConfig;
  }

  /**
   * Returns currency formatting settings for table column.
   * @returns Table column settings.
   */
  private static getCurrencySettings() {
    return {
      headerHozAlign: "right",
      hozAlign: "right",
      formatter: "money",
      formatterParams: {
        symbol: "€",
        symbolAfter: true,
        decimal: ",",
        thousand: " ",
      }
    };
  }

  /**
   * Returns dimensions row data for the report.
   * @param calcs Array of calculations.
   * @param allDimensions All dimensions in the system.
   * @returns Data in an array.
   */
  private static getData(calcs: Calculation[], allDimensions: any[]): Calculation[] {
    const result: any[] = [];

    calcs.forEach(calc => {
      const mainRow = {
        worker: calc.worker?.avatar?.displayName,
        total: calc.result?.totals?.total,
        _children: []
      };

      calc.result?.irRows?.forEach((row: IrRow) => {
        const resultRow = {
          rowType: row.data?.calculationRowType,
          type: row.type,
          irCode: row.irData?.code,
          count: row.count,
          price: row.price,
          total: (row.count ?? 1) * (row.price ?? 0),
          _children: [] as any[]
        };

        let calcDimensions: CostAccountingDimension[];
        if (row.accounting?.dimensions && row.accounting.dimensions.length > 0) {
          calcDimensions = row.accounting.dimensions;
        } else {
          calcDimensions = calc.accounting?.dimensions as CostAccountingDimension[];
        }

        // Dimensions cause split rows.
        if (calcDimensions.length > 0) {
          const resultRows: any = [];
          const splitRows = this.getSplitRows(calcDimensions, allDimensions, row);

          splitRows.forEach(pctRow => {
            const rowData = {};
            pctRow.forEach(costCenterData => {
              const label = costCenterData.label;
              const value = costCenterData.value;
              rowData[label] = value;
            });
            resultRows.push(rowData);
          });

          (resultRow as { _children: any[] })._children.push(...resultRows);
        }

        (mainRow as any)._children.push(resultRow);
      });

      result.push(mainRow);
    });
    return result;
  }

  /**
   * Splits a row into dimension rows which have a percentage weight calculated for the sum.
   * @param calcDimensions Dimensions to calculate.
   * @param sysDims All dimensions in the system, used for header labeling.
   * @param row The row whose sums are to be calculated.
   * @returns Split rows array.
   */
  private static getSplitRows(calcDimensions: any, sysDims: any[], row: IrRow): any[] {
    const groupsMap = new Map<string, CostAccountingDimension[]>();

    for (const dimension of calcDimensions) {
      if (!groupsMap.has(dimension.id)) {
        groupsMap.set(dimension.id, []);
      }
      groupsMap.get(dimension.id)!.push(dimension);
    }

    const groupArrays: CostAccountingDimension[][] = [...groupsMap.values()];

    let combinations: any[] = [[]];
    for (const group of groupArrays) {
      const next: CostAccountingDimension[][] = [];
      for (const combination of combinations) {
        for (const item of group) {
          next.push([...combination, item]);
        }
      }
      combinations = next;
    }

    const splitRows = combinations.map(combination => {
      let percentProduct = 1;
      for (const d of combination) {
        percentProduct *= d.percent ?? 1;
      }

      const rowDimensions: { label: string; value: number }[] = [];
      for (const d of combination) {
        const labelText = sysDims.find(dim => dim.field == d.value)?.label ?? "";
        rowDimensions.push({
          label: labelText,
          value: (row.price ?? 0) * percentProduct
        });
      }
      return rowDimensions;
    });

    return splitRows;
  }

  /**
   * Creates the excel rows out of generated data.
   * @param config Generated config (data, headers, options).
   * @returns Flat array containing excel header and data rows.
   */
  public static createCostCenterExcelRows(config: any): any[] {
    const excelHeaders = config.include;
    const keys = excelHeaders.filter((h: any) => h.field !== "_children").map((h: any) => h.field);
    const headerRow = excelHeaders.filter((h: any) => h.field !== "_children").map((h: any) => h.title);
    const excelData: any[] = [headerRow];

    /** Small function to round numerical values to 2 decimals. */
    function formatValue(key: string, value: any) {
      if (key == "price" || key == "total") {
        return Numeric.round(value);
      } else {
        return value;
      }
    }

    config.data.forEach((rowData: any) => {
      excelData.push(keys.map(key => formatValue(key, rowData?.[key])));

      rowData._children?.forEach((childDataRow: any) => {
        const childRow = keys.map(key => formatValue(key, childDataRow?.[key]));
        excelData.push(childRow);

        childDataRow._children?.forEach((splitRows: any) => {
          const splitRow = keys.map(key => formatValue(key, splitRows?.[key]));
          excelData.push(splitRow);
        });
      });
    });

    return excelData;
  }
}